import React, { useMemo } from 'react';
import { Box, Portal, Select as ChakraSelect, Text, createListCollection, SystemStyleObject } from '@chakra-ui/react';
import { floatingLabelStyles } from './floating-label-styles';
import { useGlobalContext } from '../../context/globalContext';

export type SelectFieldOption = {
  label: string;
  value: string;
};

export type SelectAppearance = 'default' | 'outlined' | 'sidebar';

export type SelectFieldProps = {
  options: SelectFieldOption[];
  value: string;
  onValueChange: (value: string) => void;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  layout?: 'form' | 'inline';
  width?: SystemStyleObject['width'];
  margin?: SystemStyleObject['margin'];
  padding?: SystemStyleObject['padding'];
  size?: 'sm' | 'md' | 'lg';
  /** When false, show a normal label above the field (`layout="form"` only). Ignored when `label` is omitted. */
  floatLabel?: boolean;
  /**
   * `outlined` — bordered trigger (forms, toolbars). `sidebar` — same plus sidebar indicator tint (site switcher).
   */
  appearance?: SelectAppearance;
  showClear?: boolean;
  /** Remove trigger border/radius for use inside a shared-border control group. */
  borderless?: boolean;
  /** Accessible name when the visible label is omitted, as in the refresh-interval control. */
  ariaLabel?: string;
};

export type SelectProps = SelectFieldProps;

function outlineTriggerProps(borderless?: boolean): SystemStyleObject {
  if (borderless) {
    return { border: 'none', borderRadius: 0, cursor: 'pointer' as const };
  }
  return {
    borderWidth: '1px',
    borderColor: 'global.border',
    borderRadius: 'md',
    cursor: 'pointer' as const,
  };
}

export function Select(props: SelectFieldProps) {
  const {
    options,
    value,
    onValueChange,
    label,
    placeholder,
    disabled = false,
    layout = 'form',
    width,
    margin,
    padding,
    size = 'sm',
    floatLabel = true,
    appearance = 'default',
    showClear = false,
    borderless = false,
    ariaLabel,
  } = props;
  const { portalContainerRef } = useGlobalContext();
  const collection = useMemo(() => createListCollection({ items: options }), [options]);

  const hasValue = typeof value === 'string' && value.trim().length > 0;

  const isInline = layout === 'inline';
  const useFloatingLabel = layout === 'form' && label != null && floatLabel;

  const useOutlineTrigger = appearance === 'outlined' || appearance === 'sidebar';
  const triggerSx: SystemStyleObject = useOutlineTrigger ? outlineTriggerProps(borderless) : {};
  const indicatorGroup = (
    <ChakraSelect.IndicatorGroup>
      {showClear && <ChakraSelect.ClearTrigger />}
      <ChakraSelect.Indicator colorPalette={appearance === 'sidebar' ? 'sidebar' : ''} />
    </ChakraSelect.IndicatorGroup>
  );
  /** Inside the trigger when nothing is selected: placeholder if any, otherwise empty. */
  const emptyStatePlaceholder = !hasValue
    ? placeholder !== undefined && placeholder !== ''
      ? placeholder
      : undefined
    : undefined;

  const inlineRootProps = isInline
    ? {
        display: 'flex' as const,
        flexDirection: 'row' as const,
        alignItems: 'center' as const,
        justifyContent: 'start' as const,
        gap: 2,
        padding,
        margin,
        width,
      }
    : {};

  const formRootWidth = !isInline ? (width ?? '100%') : undefined;

  return (
    <ChakraSelect.Root
      collection={collection}
      value={hasValue ? [value] : []}
      onValueChange={e => onValueChange(e.value[0] ?? '')}
      disabled={disabled}
      w={formRootWidth}
      size={size}
      margin={!isInline ? margin : undefined}
      {...inlineRootProps}
    >
      {useFloatingLabel && label != null ? (
        <Box position='relative' w='100%' p={!isInline ? padding : undefined}>
          <ChakraSelect.Control w='100%'>
            <ChakraSelect.Trigger
              {...triggerSx}
              w='100%'
              aria-label={ariaLabel ?? (typeof label === 'string' ? label : undefined)}
              data-float={useFloatingLabel || undefined}
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <ChakraSelect.ValueText placeholder={emptyStatePlaceholder} />
            </ChakraSelect.Trigger>
            {indicatorGroup}
          </ChakraSelect.Control>
          <Text as='span' css={floatingLabelStyles} data-float={useFloatingLabel || undefined}>
            {label}
          </Text>
        </Box>
      ) : (
        <>
          {label != null && (
            <ChakraSelect.Label fontSize={isInline ? undefined : 'sm'} mb={isInline ? undefined : 1}>
              {label}
            </ChakraSelect.Label>
          )}
          <ChakraSelect.Control w={isInline ? (width ?? '100%') : '100%'}>
            <ChakraSelect.Trigger {...triggerSx} w='100%' aria-label={ariaLabel}>
              <ChakraSelect.ValueText placeholder={hasValue ? undefined : placeholder} />
            </ChakraSelect.Trigger>
            {indicatorGroup}
          </ChakraSelect.Control>
        </>
      )}
      <Portal container={portalContainerRef}>
        <ChakraSelect.Positioner>
          <ChakraSelect.Content>
            {collection.items.map(item => (
              <ChakraSelect.Item key={item.value} item={item}>
                {item.label}
                <ChakraSelect.ItemIndicator />
              </ChakraSelect.Item>
            ))}
          </ChakraSelect.Content>
        </ChakraSelect.Positioner>
      </Portal>
    </ChakraSelect.Root>
  );
}
